import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Lock, KeyRound, Timer } from "lucide-react";
import { approval } from "@/data/fixtures";
import { usePersona, authorityLabels } from "@/context/PersonaContext";

/**
 * Approval gate modal · SP-01 · reusable across all features.
 *
 * Signature discipline:
 *   · Cold start (no fresh signature) → primary click reveals the
 *     "Re-authenticate to sign" row; Confirm identity fires onApprove
 *     and starts the signing session.
 *   · Fresh signing session → the info row shows the active session
 *     with a countdown; the primary button fires onApprove directly
 *     (single click) because the session covers this action.
 *
 * Role gating: if requiredAuthority is provided and the active persona
 * does not hold it (via own authorities or an active delegation), the
 * primary button renders disabled with helper text.
 */
export default function ApprovalModal({
  open,
  onCancel,
  onApprove,
  title = "Human approval required",
  rationale = "This action changes a production commitment. ManufacturingOS does not write to source systems without an explicit, permissioned, logged trigger.",
  approver: approverProp,
  action = approval.action,
  systemsAffected = approval.systemsAffected,
  auditNote = "Will be logged to audit trail on approval.",
  confirmLabel = "Approve and log",
  requiredAuthority = null,
}) {
  const {
    active,
    hasAuthority,
    signatureFresh,
    lastAuthAtLabel,
    formatSessionCountdown,
    noteAuthentication,
  } = usePersona();
  const [stepUp, setStepUp] = useState(false);

  useEffect(() => {
    if (!open) setStepUp(false);
  }, [open]);

  const gated = !!requiredAuthority && !hasAuthority(requiredAuthority);
  const helper = gated ? authorityLabels[requiredAuthority] : null;
  const approver = approverProp || active?.name || approval.approver;

  const onPrimaryClick = () => {
    if (gated) return;
    if (signatureFresh) {
      noteAuthentication();
      onApprove();
    } else {
      setStepUp(true);
    }
  };

  const onConfirmIdentity = () => {
    noteAuthentication();
    onApprove();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onCancel())}>
      <DialogContent
        className="max-w-[580px] p-0 gap-0 overflow-hidden"
        style={{ background: "var(--mos-surface)" }}
        data-testid="approval-modal"
      >
        <div
          className="px-6 pt-6 pb-5 border-b"
          style={{ borderColor: "var(--mos-border)" }}
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span
                className="h-9 w-9 rounded-full inline-flex items-center justify-center"
                style={{ background: "#EFF3F9" }}
              >
                <ShieldCheck size={17} style={{ color: "var(--mos-accent)" }} />
              </span>
              <DialogTitle className="text-[18px] font-semibold text-slate-900">
                {title}
              </DialogTitle>
            </div>
          </DialogHeader>
          <p className="mt-4 text-[13.5px] text-slate-600 leading-relaxed">
            {rationale}
          </p>
        </div>

        <div className="px-6 py-5 space-y-3.5">
          <Row label="Approver" value={approver} />
          <Row label="Action" value={action} />
          <Row
            label="Systems affected"
            value={
              <span className="flex flex-wrap gap-1.5">
                {systemsAffected.map((s) => (
                  <span
                    key={s}
                    className="mos-mono text-[12px] px-2 py-0.5 rounded border"
                    style={{
                      background: "#F1F5F9",
                      borderColor: "var(--mos-border)",
                      color: "var(--mos-text)",
                    }}
                  >
                    {s}
                  </span>
                ))}
              </span>
            }
          />
          <Row
            label="Audit"
            value={<span className="text-slate-700">{auditNote}</span>}
          />
        </div>

        {/* Fresh signing session · visible when session covers this action */}
        {signatureFresh && !gated && (
          <div
            className="px-6 py-3 border-t flex items-center gap-3"
            style={{
              background: "#ECFDF5",
              borderColor: "var(--mos-green-border)",
              color: "#065F46",
            }}
            data-testid="approval-session-active"
          >
            <Timer size={13} />
            <span className="text-[12.5px]">
              Signing session active · signed at{" "}
              <span className="mos-mono">{lastAuthAtLabel}</span> · expires in{" "}
              <span
                className="mos-mono"
                data-testid="approval-session-countdown"
              >
                {formatSessionCountdown()}
              </span>
            </span>
          </div>
        )}

        {/* Step-up confirmation row · cold-start signature */}
        {stepUp && !gated && !signatureFresh && (
          <div
            className="px-6 py-4 border-t"
            style={{
              background: "#F8FAFC",
              borderColor: "var(--mos-border)",
              animation: "mos-fade-in 180ms ease-out both",
            }}
            data-testid="approval-stepup-row"
          >
            <div className="flex items-center gap-3">
              <span
                className="h-8 w-8 rounded-full flex items-center justify-center flex-none"
                style={{ background: "#EFF3F9" }}
              >
                <KeyRound size={14} style={{ color: "var(--mos-accent)" }} />
              </span>
              <div className="flex-1">
                <div className="text-[12.5px] font-medium text-slate-900">
                  Re-authenticate to sign
                </div>
                <div className="text-[11.5px] text-slate-500 mt-0.5">
                  Signing rebinds this action to a fresh authentication event.
                </div>
              </div>
              <button
                type="button"
                onClick={onConfirmIdentity}
                className="mos-btn mos-btn-primary flex-none"
                data-testid="btn-confirm-identity"
              >
                Confirm identity · {active?.name || approver}
              </button>
            </div>
          </div>
        )}

        {/* Gating helper */}
        {gated && (
          <div
            className="px-6 py-3 border-t text-[12px]"
            style={{
              background: "#FEF3C7",
              borderColor: "var(--mos-amber-border)",
              color: "#78350F",
            }}
            data-testid="approval-gated-helper"
          >
            <span className="inline-flex items-center gap-2">
              <Lock size={12} />
              {helper}
            </span>
          </div>
        )}

        <div
          className="px-6 py-4 border-t flex items-center justify-end gap-3"
          style={{ background: "#FAFBFC", borderColor: "var(--mos-border)" }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="mos-btn mos-btn-secondary"
            data-testid="btn-cancel-approval"
          >
            Cancel
          </button>
          {!stepUp && (
            <button
              type="button"
              onClick={onPrimaryClick}
              disabled={gated}
              className={
                "mos-btn " +
                (gated
                  ? "mos-btn-secondary cursor-not-allowed opacity-60"
                  : "mos-btn-primary")
              }
              data-testid="btn-approve-and-log"
              aria-disabled={gated}
            >
              {signatureFresh && !gated ? "Sign and log" : confirmLabel}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-6">
      <div className="w-40 text-[12px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="flex-1 text-[13.5px] text-slate-900">{value}</div>
    </div>
  );
}
